// Tenantra Inc. - Properties Management Page (Microsoft 365 Admin Style)
import { useEffect, useState } from 'react';
import { 
  Building2, Home, Plus, Search, RefreshCw, ChevronDown,
  CheckCircle, Clock, AlertTriangle, XCircle, Users, Archive
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { useUrlParam } from '@/services/useUrlParam';
import PaywallModal from '@/components/PaywallModal';
import SubscriptionGate from '@/components/SubscriptionGate';
import { usePaywall } from '@/hooks/usePaywall';
import { TableSkeleton, SortableHeader, useSort, exportCsv, ExportButton, RowOpenButton } from '@/components/dashboard/TableKit';
import { PropertyPanel } from '@/components/dashboard/PropertyPanel';

// Property type labels
const PROPERTY_TYPES = {
  house: 'House',
  apartment: 'Apartment',
  condo: 'Condo',
  townhouse: 'Townhouse',
  other: 'Other'
};

// Status config
const STATUS_CONFIG = {
  verified: { label: 'Verified', dotColor: 'bg-emerald-500', textColor: 'text-emerald-600' },
  pending: { label: 'Pending', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  incomplete: { label: 'Incomplete', dotColor: 'bg-slate-400', textColor: 'text-slate-600' },
  needs_documents: { label: 'Needs docs', dotColor: 'bg-amber-500', textColor: 'text-amber-600' },
  manual_review: { label: 'In review', dotColor: 'bg-blue-500', textColor: 'text-blue-600' },
  rejected: { label: 'Rejected', dotColor: 'bg-red-500', textColor: 'text-red-600' },
  failed: { label: 'Failed', dotColor: 'bg-red-500', textColor: 'text-red-600' }
};

// Empty State
function EmptyState({ onAddClick }) {
  return (
    <div className="text-center py-12">
      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
        <Home className="w-6 h-6 text-slate-400" />
      </div>
      <h3 className="text-base font-medium text-slate-900 mb-1">No properties yet</h3>
      <p className="text-sm text-slate-500 max-w-sm mx-auto mb-4">
        Add your first rental property to start reporting tenant behaviour.
      </p>
      <a 
        href="/landlord/properties/new" 
        onClick={onAddClick}
        className="text-sm text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1"
      >
        <Plus className="w-4 h-4" />
        Add property
      </a>
    </div>
  );
}

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [tenancyCounts, setTenancyCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useUrlParam('status');
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [openProperty, setOpenProperty] = useState(null);
  const [openParam, setOpenParam] = useUrlParam('open', '');
  useEffect(() => {
    if (!openParam || !properties.length) return;
    const p = properties.find(x => x.id === openParam);
    if (p) setOpenProperty(p);
    setOpenParam('');
  }, [openParam, properties]); // eslint-disable-line
  
  // Paywall hook
  const { paywallState, hidePaywall, handlePaymentError, showPaywall } = usePaywall();
  const [canAddProperty, setCanAddProperty] = useState(true);
  const [billingPlan, setBillingPlan] = useState(null);

  useEffect(() => {
    fetchProperties();
    checkBillingStatus();
  }, [statusFilter]);
  
  const checkBillingStatus = async () => {
    try {
      const response = await api.get('/billing/current-plan');
      setBillingPlan(response.data);
      setCanAddProperty(response.data.can_add_property);
    } catch (err) {
      console.error('Failed to fetch billing status:', err);
    }
  };
  
  const handleAddPropertyClick = (e) => {
    if (!canAddProperty) {
      e.preventDefault();
      showPaywall({
        action: 'add_property',
        planRequired: billingPlan?.plan === 'none' ? 'starter' : 'professional',
        message: billingPlan?.plan === 'none' 
          ? 'Subscribe to start adding properties' 
          : 'Property limit reached. Upgrade to add more properties.'
      });
    }
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setSubscriptionRequired(false);
      const params = { limit: 500 };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const [propertiesRes, tenanciesRes] = await Promise.all([
        api.get('/properties', { params }),
        api.get('/tenancies')
      ]);
      
      setProperties(propertiesRes.data.items || []);
      
      const counts = {};
      (tenanciesRes.data.items || []).forEach(t => {
        if (!counts[t.property_id]) counts[t.property_id] = 0;
        counts[t.property_id]++;
      });
      setTenancyCounts(counts);
    } catch (err) {
      // Handle 402 Payment Required - show subscription gate
      if (err.response?.status === 402) {
        setSubscriptionRequired(true);
        setSubscriptionMessage(err.response?.data?.detail?.message || 'An active subscription is required to view your properties.');
        return;
      }
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (property) => setOpenProperty(property);

  const filteredProperties = properties.filter(p => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      p.address.street.toLowerCase().includes(search) ||
      p.address.city.toLowerCase().includes(search) ||
      p.address.postal_code.toLowerCase().includes(search)
    );
  });

  const PROPERTY_COLUMNS = [
    { key: 'address', label: 'Address', span: 3 },
    { key: 'city', label: 'City', span: 2 },
    { key: 'type', label: 'Type', span: 2 },
    { key: 'tenants', label: 'Tenants', span: 2 },
    { key: 'status', label: 'Status', span: 2 },
    { key: 'open', label: '', span: 1, sortable: false },
  ];
  const { sorted: sortedProperties, sort, toggle: toggleSort } = useSort(filteredProperties, {
    address: (p) => p.address.street,
    city: (p) => p.address.city,
    type: (p) => PROPERTY_TYPES[p.property_type] || 'Other',
    tenants: (p) => tenancyCounts[p.id] || 0,
    status: (p) => (STATUS_CONFIG[p.verification_status] || STATUS_CONFIG.pending).label,
  });
  const handleExport = () => exportCsv('tenantra-properties.csv', sortedProperties, [
    { label: 'Address', value: (p) => p.address.street },
    { label: 'Unit', value: (p) => p.address.unit_number || '' },
    { label: 'City', value: (p) => p.address.city },
    { label: 'Province', value: (p) => p.address.province || '' },
    { label: 'Postal code', value: (p) => p.address.postal_code },
    { label: 'Type', value: (p) => PROPERTY_TYPES[p.property_type] || 'Other' },
    { label: 'Units', value: (p) => (p.units || []).map(u => u.number).join(' ') },
    { label: 'Tenants', value: (p) => tenancyCounts[p.id] || 0 },
    { label: 'Status', value: (p) => (STATUS_CONFIG[p.verification_status] || STATUS_CONFIG.pending).label },
  ]);

  const stats = {
    total: properties.length,
    verified: properties.filter(p => p.verification_status === 'verified').length,
    pending: properties.filter(p => ['pending', 'manual_review', 'incomplete'].includes(p.verification_status)).length,
    needsAction: properties.filter(p => p.verification_status === 'needs_documents').length
  };

  // Show subscription gate if subscription is required
  if (subscriptionRequired) {
    return <SubscriptionGate message={subscriptionMessage} />;
  }

  return (
    <div className="space-y-5">
      {/* Header - Microsoft style */}
      <div>
        <h1 className="text-lg font-semibold text-slate-800">Properties</h1>
        <p className="text-sm text-slate-500">Manage your rental properties and verification status</p>
      </div>

      {/* Stats Row - Compact inline */}
      {properties.length > 0 && (
        <div className="flex items-center gap-6 py-2 border-b border-slate-200 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold text-slate-800">{stats.total}</span>
            <span className="text-sm text-slate-500">Total</span>
          </div>
          <div className="w-px h-5 bg-slate-200" />
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold text-emerald-600">{stats.verified}</span>
            <span className="text-sm text-slate-500">Verified</span>
          </div>
          <div className="w-px h-5 bg-slate-200" />
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold text-amber-600">{stats.pending}</span>
            <span className="text-sm text-slate-500">Pending</span>
          </div>
          {stats.needsAction > 0 && (
            <>
              <div className="w-px h-5 bg-slate-200" />
              <div className="flex items-center gap-2">
                <span className="text-xl font-semibold text-amber-600">{stats.needsAction}</span>
                <span className="text-sm text-slate-500">Needs action</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Actions Row - Text links */}
      <div className="flex items-center gap-4">
        <a 
          href="/landlord/properties/new" 
          onClick={handleAddPropertyClick}
          className="text-sm text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1.5 hover:underline"
        >
          <Plus className="w-4 h-4" />
          Add property
        </a>
        <button 
          onClick={fetchProperties}
          className="text-sm text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1.5 hover:underline"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
        <ExportButton onClick={handleExport} disabled={loading || sortedProperties.length === 0} testId="properties-export-btn" />
      </div>

      {/* Search and Filters - Compact */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 pl-9 text-sm bg-white border-slate-200 placeholder:text-slate-400"
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 pr-7 bg-white border border-slate-200 rounded-md text-slate-600 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 appearance-none cursor-pointer"
          >
            <option value="all">All status</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
            <option value="needs_documents">Needs docs</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Mobile FAB */}
      <a 
        href="/landlord/properties/new"
        onClick={handleAddPropertyClick}
        className="sm:hidden fixed bottom-6 right-6 z-40 w-12 h-12 bg-teal-600 rounded-full shadow-lg flex items-center justify-center text-white active:scale-95"
      >
        <Plus className="w-5 h-5" />
      </a>

      {/* Properties Table - Microsoft style */}
      {loading ? (
        <TableSkeleton rows={8} spans={[3, 2, 2, 2, 2, 1]} />
      ) : filteredProperties.length === 0 ? (
        searchTerm || statusFilter !== 'all' ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-slate-800 mb-1">No results</h3>
            <p className="text-sm text-slate-500 mb-3">Try adjusting your search or filters</p>
            <button 
              onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <EmptyState onAddClick={handleAddPropertyClick} />
        )
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg">
          <SortableHeader columns={PROPERTY_COLUMNS} sort={sort} onSort={toggleSort} />

          {/* Table Rows */}
          <div className="divide-y divide-slate-100">
            {sortedProperties.map((property) => {
              const statusConfig = STATUS_CONFIG[property.verification_status] || STATUS_CONFIG.pending;
              const tenantCount = tenancyCounts[property.id] || 0;
              
              return (
                <div
                  key={property.id}
                  onClick={() => handleRowClick(property)}
                  className="lens lens-row grid grid-cols-12 gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors items-center cursor-pointer"
                  data-testid={`property-row-${property.id}`}
                >
                  {/* Address with dot */}
                  <div className="col-span-12 sm:col-span-3 flex items-center gap-2 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusConfig.dotColor}`} />
                    <span className="text-xs font-medium text-slate-800 truncate">{property.address.street}</span>
                    <span className="sm:hidden text-[10px] text-slate-400 ml-auto">{property.address.city}</span>
                  </div>
                  
                  {/* City */}
                  <div className="hidden sm:block col-span-2">
                    <span className="text-xs text-slate-500">{property.address.city}</span>
                  </div>
                  
                  {/* Type */}
                  <div className="hidden sm:block col-span-2">
                    <span className="text-xs text-slate-500" data-testid={`property-type-${property.id}`}>{PROPERTY_TYPES[property.property_type] || 'Other'}{property.units?.length > 1 ? ` · ${property.units.length} units` : ''}</span>
                  </div>
                  
                  {/* Tenants */}
                  <div className="hidden sm:block col-span-2">
                    <span className="text-xs text-slate-500">{tenantCount}</span>
                  </div>
                  
                  {/* Status */}
                  <div className="hidden sm:block col-span-2">
                    <span className={`text-xs ${statusConfig.textColor}`}>{statusConfig.label}</span>
                  </div>

                  <div className="hidden sm:flex col-span-1 justify-end">
                    <RowOpenButton onClick={() => handleRowClick(property)} testId={`property-open-${property.id}`} label={`Open ${property.address.street}`} />
                  </div>

                  {/* Mobile bottom */}
                  <div className="sm:hidden col-span-12 flex items-center justify-between -mt-0.5">
                    <span className="text-[10px] text-slate-400">{PROPERTY_TYPES[property.property_type] || 'Other'}{property.units?.length > 1 ? ` · ${property.units.length} units` : ''} · {tenantCount} tenants</span>
                    <span className={`text-[10px] ${statusConfig.textColor}`}>{statusConfig.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <PropertyPanel property={openProperty} typeLabel={openProperty ? (PROPERTY_TYPES[openProperty.property_type] || 'Other') : ''} onClose={() => setOpenProperty(null)} onUnitsChange={(units) => { setOpenProperty(p => ({ ...p, units })); setProperties(ps => ps.map(x => x.id === openProperty.id ? { ...x, units } : x)); }} />

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={paywallState.isOpen}
        onClose={hidePaywall}
        action={paywallState.action}
        planRequired={paywallState.planRequired}
        message={paywallState.message}
      />
    </div>
  );
}
